import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  Utensils,
  Gift,
  Heart,
  Trophy,
  ArrowRight,
  Download,
  Share2,
  CheckCircle2,
  CalendarPlus,
  Navigation,
  Mail,
} from "lucide-react";
import { PageHero } from "@/components/Section";
import { church, fullAddress } from "@/config/church";
import { api } from "@/lib/api";
import { fireGoogleAdsConversion } from "@/lib/analytics";
import { toast } from "sonner";
import useReveal from "@/hooks/useReveal";
import FamilyDayFlyer from "@/components/FamilyDayFlyer";

const EVENT = {
  title: "Friends & Family Day",
  tagline: "Faith · Fellowship · Family · Fun",
  invite: "Bring Your Family. Invite Your Friends. Everyone Is Welcome!",
  dateLabel: "Sunday, October 11, 2026",
  timeLabel: "11:00 AM Worship Service",
  isoDate: "2026-10-11",
  isoStart: "2026-10-11T11:00:00-05:00",
  isoEnd: "2026-10-11T14:00:00-05:00",
};

const perks = [
  {
    icon: Trophy,
    title: "Largest Family Group",
    body: "$100 cash reward for the largest family group that brings out for worship.",
  },
  {
    icon: Utensils,
    title: "Fellowship Meal",
    body: "Stay for a shared meal right after service — food, laughter, and family.",
  },
  {
    icon: Gift,
    title: "Door Prizes & Giveaways",
    body: "Doors, hearts, and hands wide open — with fun surprises for our guests.",
  },
  {
    icon: Heart,
    title: "Worship · Fellowship · Fun",
    body: "A Spirit-filled service and a warm welcome home for every guest.",
  },
];

// Public RSVP URL — used to generate the QR code
const RSVP_URL = "https://newphiladelphiabaptist.org/family-day";
// api.qrserver.com is free, no key, returns PNG
const QR_SRC = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(
  RSVP_URL
)}&size=600x600&margin=12&format=png&bgcolor=ffffff&color=1a1a1a`;

// Calendar helpers — event is Oct 11, 2026, 11:00 AM CDT (UTC-5) to 2:00 PM CDT
const CAL_START_UTC = "20261011T160000Z"; // 11:00 AM CDT
const CAL_END_UTC = "20261011T190000Z"; // 2:00 PM CDT (includes fellowship meal)
const CAL_TITLE = "Friends & Family Day — New Philadelphia MBC";
const CAL_LOCATION = "533 S. Mendenhall Rd., Memphis, TN 38117";
const CAL_DESC =
  "Bring your family. Invite your friends. Everyone is welcome! " +
  "11 AM worship service, followed by a fellowship meal, door prizes, and giveaways. " +
  "RSVP at https://newphiladelphiabaptist.org/family-day";

const googleCalendarUrl = () =>
  `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
    CAL_TITLE
  )}&dates=${CAL_START_UTC}/${CAL_END_UTC}&details=${encodeURIComponent(
    CAL_DESC
  )}&location=${encodeURIComponent(CAL_LOCATION)}`;

const buildIcsBlobUrl = () => {
  const now = new Date()
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}/, "");
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//NPMBC//Family Day//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:family-day-2026@newphiladelphiabaptist.org`,
    `DTSTAMP:${now}`,
    `DTSTART:${CAL_START_UTC}`,
    `DTEND:${CAL_END_UTC}`,
    `SUMMARY:${CAL_TITLE}`,
    `LOCATION:${CAL_LOCATION}`,
    `DESCRIPTION:${CAL_DESC.replace(/\n/g, "\\n")}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  return URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
};

const directionsUrl = () =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(CAL_LOCATION)}`;

export default function FamilyDay() {
  useReveal();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    group_size: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [submitted, setSubmitted] = useState({ name: "", email: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) {
      toast.error("Please share your name so we can save you a seat.");
      return;
    }
    try {
      setBusy(true);
      const { data } = await api.post("/registration", {
        kind: "family-day",
        name: form.name,
        email: form.email || null,
        phone: form.phone || null,
        details: {
          event: EVENT.title,
          event_date: EVENT.isoDate,
          group_size: form.group_size || null,
        },
        notes: form.notes || null,
      });
      // Fire Google Ads conversion (harmless if gtag missing)
      fireGoogleAdsConversion("family-day");
      toast.success(data.message || "You're on the list — see you October 11!");
      setSubmitted({ name: form.name, email: form.email });
      setDone(true);
    } catch {
      toast.error("Could not save your RSVP. Please try again or call the church.");
    } finally {
      setBusy(false);
    }
  };

  const shareText = useMemo(
    () =>
      `Friends & Family Day at New Philadelphia Missionary Baptist Church — ${EVENT.dateLabel} · ${EVENT.timeLabel}. RSVP at ${RSVP_URL}`,
    []
  );

  const share = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: EVENT.title, text: shareText, url: RSVP_URL });
        return;
      } catch (_) {}
    }
    try {
      await navigator.clipboard.writeText(RSVP_URL);
      toast.success("RSVP link copied to clipboard.");
    } catch {
      toast.error("Could not copy link.");
    }
  };

  return (
    <div data-testid="family-day-page">
      <PageHero
        eyebrow="Special Sunday"
        title="Friends & Family Day"
        subtitle={`${EVENT.invite} — ${EVENT.dateLabel} at ${EVENT.timeLabel}.`}
        image="https://customer-assets-eiarnc6j.emergentagent.net/job_strange-cartwright-8/artifacts/bf7g84zx_IMG_5577.jpg"
      />

      {/* Tagline band */}
      <section className="bg-npbc-gold/95 text-npbc-black">
        <div className="container-npbc py-4 text-center text-[11px] sm:text-xs font-bold uppercase tracking-[0.32em]">
          {EVENT.tagline}
        </div>
      </section>

      {/* Event details + perks */}
      <section className="bg-white py-20 md:py-28">
        <div className="container-npbc">
          <div className="grid gap-10 lg:grid-cols-[1fr_1.1fr] items-start">
            <div data-reveal>
              <div className="eyebrow">The Details</div>
              <h2 className="mt-3 heading-serif text-4xl md:text-5xl leading-[1.05]">
                One Sunday. One family. One welcome.
              </h2>
              <p className="mt-5 text-npbc-gray/85 leading-relaxed">
                On <strong>{EVENT.dateLabel}</strong>, we're throwing open our doors and setting an extra
                place at the table. Bring your people — old friends, new neighbors, cousins you haven't seen
                in years. There is a seat, a hug, and a plate waiting.
              </p>
              <ul className="mt-8 space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 mt-0.5 text-npbc-red" />
                  <div>
                    <div className="font-semibold text-npbc-black">{EVENT.dateLabel}</div>
                    <div className="text-npbc-gray/70">{EVENT.timeLabel}</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 mt-0.5 text-npbc-red" />
                  <div>
                    <div className="font-semibold text-npbc-black">{church.name}</div>
                    <div className="text-npbc-gray/70">{fullAddress}</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Clock className="h-5 w-5 mt-0.5 text-npbc-red" />
                  <div>
                    <div className="font-semibold text-npbc-black">Fellowship meal after service</div>
                    <div className="text-npbc-gray/70">Stay a while, break bread with us.</div>
                  </div>
                </li>
              </ul>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a
                  href="#rsvp"
                  data-testid="family-day-rsvp-anchor-btn"
                  className="btn-primary !py-3 !px-6 text-sm"
                >
                  RSVP Now
                  <ArrowRight className="h-4 w-4" />
                </a>
                <button
                  type="button"
                  onClick={share}
                  data-testid="family-day-share-btn"
                  className="btn-ghost-dark !py-3 !px-6 text-sm"
                >
                  <Share2 className="h-4 w-4" />
                  Share
                </button>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2" data-reveal>
              {perks.map((p, i) => (
                <div
                  key={p.title}
                  data-testid={`family-day-perk-${i}`}
                  style={{ transitionDelay: `${i * 60}ms` }}
                  className="rounded-2xl bg-white border border-black/[0.06] p-6 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_20px_60px_-30px_rgba(0,0,0,0.18)]"
                >
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-npbc-red/10 text-npbc-red">
                    <p.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 heading-serif text-xl">{p.title}</h3>
                  <p className="mt-2 text-sm text-npbc-gray/85 leading-relaxed">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* RSVP + QR */}
      <section id="rsvp" className="bg-npbc-cream py-20 md:py-28">
        <div className="container-npbc grid gap-12 lg:grid-cols-[1.2fr_1fr] items-start">
          <div data-reveal>
            <div className="eyebrow">RSVP</div>
            <h2 className="mt-3 heading-serif text-4xl md:text-5xl leading-[1.05]">
              Save your seat.
            </h2>
            <p className="mt-4 text-npbc-gray/85">
              Let us know you're coming so we can prepare a warm welcome and enough food for everyone.
            </p>

            {done ? (
              <div
                data-testid="family-day-rsvp-success"
                className="mt-8 rounded-2xl bg-white border border-npbc-red/20 p-8 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.25)]"
              >
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-npbc-gold/20">
                  <CheckCircle2 className="h-6 w-6 text-npbc-red" />
                </div>
                <h3 className="mt-4 heading-serif text-3xl md:text-4xl leading-tight">
                  We saved your seat
                  {submitted.name ? `, ${submitted.name.trim().split(" ")[0]}` : ""}!
                </h3>
                <p className="mt-3 text-npbc-gray/85 text-sm leading-relaxed">
                  <strong>Sunday, October 11, 2026 · 11:00 AM</strong>
                  <br />
                  533 S. Mendenhall Rd., Memphis, TN 38117
                </p>

                {submitted.email && (
                  <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-npbc-cream px-4 py-2 text-xs text-npbc-gray/80">
                    <Mail className="h-3.5 w-3.5 text-npbc-red" />
                    A confirmation is on its way to{" "}
                    <strong className="text-npbc-black">{submitted.email}</strong>
                  </div>
                )}

                {/* Action grid */}
                <div className="mt-7 grid gap-3 sm:grid-cols-2">
                  <a
                    href={googleCalendarUrl()}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid="family-day-add-google-cal"
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-black/10 bg-white px-4 py-3 text-sm font-semibold text-npbc-black hover:bg-npbc-cream transition"
                  >
                    <CalendarPlus className="h-4 w-4 text-npbc-red" />
                    Add to Google Calendar
                  </a>
                  <a
                    href={buildIcsBlobUrl()}
                    download="npmbc-family-day.ics"
                    data-testid="family-day-add-apple-cal"
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-black/10 bg-white px-4 py-3 text-sm font-semibold text-npbc-black hover:bg-npbc-cream transition"
                  >
                    <CalendarPlus className="h-4 w-4 text-npbc-red" />
                    Add to Apple / Outlook
                  </a>
                  <a
                    href={directionsUrl()}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid="family-day-get-directions"
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-black/10 bg-white px-4 py-3 text-sm font-semibold text-npbc-black hover:bg-npbc-cream transition"
                  >
                    <Navigation className="h-4 w-4 text-npbc-red" />
                    Get Directions
                  </a>
                  <a
                    href={`tel:${church.phoneRaw}`}
                    data-testid="family-day-call-church"
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-black/10 bg-white px-4 py-3 text-sm font-semibold text-npbc-black hover:bg-npbc-cream transition"
                  >
                    <MapPin className="h-4 w-4 text-npbc-red" />
                    Call the Church
                  </a>
                </div>

                {/* Bring a friend */}
                <div className="mt-7 rounded-2xl bg-gradient-to-br from-npbc-red to-[#a80d26] p-6 text-white">
                  <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-npbc-gold">
                    Bring a Friend
                  </div>
                  <div className="mt-2 heading-serif text-2xl leading-snug">
                    The largest family group wins <span className="text-npbc-gold">$100.</span>
                  </div>
                  <p className="mt-2 text-white/85 text-sm leading-relaxed">
                    Every share is an invitation. Send this link to one person right now — someone who
                    needs a home to come home to.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={share}
                      data-testid="family-day-success-share-btn"
                      className="inline-flex items-center gap-2 rounded-full bg-white text-npbc-red font-semibold px-5 py-2.5 text-sm hover:bg-npbc-gold hover:text-npbc-black transition"
                    >
                      <Share2 className="h-4 w-4" />
                      Share the invite
                    </button>
                    <a
                      href={`sms:?&body=${encodeURIComponent(shareText)}`}
                      data-testid="family-day-share-sms"
                      className="inline-flex items-center gap-2 rounded-full border border-white/40 text-white font-semibold px-5 py-2.5 text-sm hover:bg-white/10 transition"
                    >
                      Text a friend
                    </a>
                  </div>
                </div>

                <div className="mt-6">
                  <Link
                    to="/"
                    className="text-sm font-semibold text-npbc-red hover:underline inline-flex items-center gap-1"
                  >
                    Back to Home <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="mt-8 grid gap-4 rounded-2xl bg-white border border-black/[0.06] p-6 md:p-8 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_20px_60px_-30px_rgba(0,0,0,0.15)]"
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-[0.16em] text-npbc-gray/80">
                      Full name *
                    </span>
                    <input
                      required
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="family-day-name-input"
                      className="mt-2 w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm outline-none focus:border-npbc-red transition"
                      placeholder="Your name"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-[0.16em] text-npbc-gray/80">
                      Phone
                    </span>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      data-testid="family-day-phone-input"
                      className="mt-2 w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm outline-none focus:border-npbc-red transition"
                      placeholder="(901) 555-0123"
                    />
                  </label>
                </div>
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-npbc-gray/80">
                    Email
                  </span>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="family-day-email-input"
                    className="mt-2 w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm outline-none focus:border-npbc-red transition"
                    placeholder="you@example.com"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-npbc-gray/80">
                    How many are coming with you?
                  </span>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={form.group_size}
                    onChange={(e) => setForm({ ...form, group_size: e.target.value })}
                    data-testid="family-day-group-size-input"
                    className="mt-2 w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm outline-none focus:border-npbc-red transition"
                    placeholder="e.g. 6"
                  />
                  <span className="mt-1 block text-[11px] text-npbc-gray/60">
                    Largest family group wins the <strong>$100 cash reward</strong>.
                  </span>
                </label>
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-npbc-gray/80">
                    Anything we should know?
                  </span>
                  <textarea
                    rows={3}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    data-testid="family-day-notes-input"
                    className="mt-2 w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm outline-none focus:border-npbc-red transition resize-none"
                    placeholder="First-time guest? Prayer request? Something you'd like us to pray about?"
                  />
                </label>
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="family-day-submit-btn"
                  className="btn-primary mt-2 justify-center w-full sm:w-auto disabled:opacity-60"
                >
                  {busy ? "Saving your seat..." : "Save My Seat"}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </form>
            )}
          </div>

          {/* QR code panel */}
          <aside
            data-reveal
            className="rounded-2xl bg-white border border-black/[0.06] p-6 md:p-8 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_20px_60px_-30px_rgba(0,0,0,0.18)]"
          >
            <div className="eyebrow">Scan to RSVP</div>
            <h3 className="mt-3 heading-serif text-2xl">Share the invitation.</h3>
            <p className="mt-2 text-sm text-npbc-gray/85 leading-relaxed">
              Scan below, or grab a ready-made flyer just underneath to print for the bulletin or post on
              social — the RSVP QR is baked right in.
            </p>
            <div className="mt-6 rounded-2xl bg-white border-2 border-npbc-black/10 p-4 flex items-center justify-center">
              <img
                src={QR_SRC}
                alt={`QR code for ${RSVP_URL}`}
                data-testid="family-day-qr-image"
                width={280}
                height={280}
                className="h-64 w-64 object-contain"
                loading="lazy"
              />
            </div>
            <div className="mt-4 rounded-xl bg-npbc-cream px-4 py-3 text-xs text-npbc-gray/80 text-center break-all">
              {RSVP_URL}
            </div>
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              <a
                href={QR_SRC}
                download="npmbc-family-day-qr.png"
                data-testid="family-day-qr-download"
                className="btn-primary !py-2.5 !px-4 text-xs justify-center"
              >
                <Download className="h-4 w-4" />
                Download QR
              </a>
              <button
                type="button"
                onClick={share}
                data-testid="family-day-share-alt-btn"
                className="btn-ghost-dark !py-2.5 !px-4 text-xs justify-center"
              >
                <Share2 className="h-4 w-4" />
                Copy Link
              </button>
            </div>
          </aside>
        </div>

        {/* Ready-made printable flyer */}
        <div className="container-npbc mt-12" data-reveal>
          <div className="rounded-3xl bg-white border border-black/[0.06] p-6 md:p-10 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_20px_60px_-30px_rgba(0,0,0,0.18)]">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
              <div>
                <div className="eyebrow">Ready to Share</div>
                <h3 className="mt-3 heading-serif text-3xl md:text-4xl leading-tight">
                  Print-ready flyer, already designed.
                </h3>
                <p className="mt-3 text-npbc-gray/85 max-w-xl leading-relaxed">
                  Pick a format, click download, and share. QR code, church info, and event details all
                  included — no design work needed.
                </p>
              </div>
            </div>
            <FamilyDayFlyer />
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="bg-npbc-black text-white py-20">
        <div className="container-npbc text-center max-w-2xl mx-auto" data-reveal>
          <div className="eyebrow !text-npbc-gold justify-center flex">Everyone Is Welcome</div>
          <h2 className="mt-3 heading-serif text-4xl md:text-5xl">
            Come as you are. Leave as family.
          </h2>
          <p className="mt-4 text-white/75">
            {EVENT.dateLabel} · {EVENT.timeLabel} · {fullAddress}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a href="#rsvp" className="btn-gold">
              RSVP for Friends & Family Day
              <ArrowRight className="h-4 w-4" />
            </a>
            <a href={`tel:${church.phoneRaw}`} className="inline-flex items-center gap-2 rounded-full border border-white/25 text-white font-semibold px-6 py-3 hover:bg-white hover:text-npbc-black transition-all duration-300">
              Call the Church · {church.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
